import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { codeItemCreateSchema, codeItemUpdateSchema } from '../schema/codeSchema';
import type { CodeItemCreateFormValues, CodeItemUpdateFormValues } from '../schema/codeSchema';
import type { CommonCodeValueResponse } from '../types/code';

interface CodeItemCreateDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  groupCode: string;
  initialData?: CommonCodeValueResponse | null;
  onClose: () => void;
  onSubmit: (values: CodeItemCreateFormValues | CodeItemUpdateFormValues) => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function CodeItemCreateDialog({
  open,
  mode,
  groupCode,
  initialData,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
}: CodeItemCreateDialogProps) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, dirtyFields },
  } = useForm<CodeItemCreateFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(isEdit ? (codeItemUpdateSchema as any) : codeItemCreateSchema),
    defaultValues: {
      sortOrder: 0,
      useYn: true,
    },
  });

  // UAT 2026-05-20 #461: codeValue 입력마다 labelKey 가 'common.code.<group>.<codeValue>' 로 실시간 동기화.
  // 사용자가 labelKey 를 직접 수정한 시점부터는 잠금(dirtyFields.labelKey 가 true) → 자동 덮어쓰기 금지.
  const watchedCodeValue = useWatch({ control, name: 'codeValue' });
  const labelKeyTouched = !!dirtyFields.labelKey;

  useEffect(() => {
    if (isEdit) return;
    if (labelKeyTouched) return;
    const next = watchedCodeValue ? `common.code.${groupCode}.${watchedCodeValue}` : '';
    setValue('labelKey', next, { shouldValidate: false, shouldDirty: false });
  }, [watchedCodeValue, isEdit, groupCode, labelKeyTouched, setValue]);

  useEffect(() => {
    if (!open) {
      reset({ sortOrder: 0, useYn: true });
    } else if (isEdit && initialData) {
      reset({
        codeValue: initialData.codeValue,
        defaultLabel: initialData.defaultLabel,
        labelKey: initialData.labelKey ?? '',
        sortOrder: initialData.sortOrder,
        useYn: initialData.useYn,
        extraAttrs: initialData.extraAttrs ?? '',
      });
    }
  }, [open, isEdit, initialData, reset]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    marginBottom: 'var(--spacing-3)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    marginTop: 'var(--spacing-1)',
  };

  const errorsTyped = errors as Partial<Record<keyof CodeItemCreateFormValues, { message?: string }>>;

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('commonCode.createItemDialog.titleEdit', { groupCode }) : t('commonCode.createItemDialog.titleCreate', { groupCode })}
      onClose={onClose}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={isLoading ? t('commonCode.createItemDialog.submitting') : t('commonCode.createItemDialog.submit')}
      cancelLabel={t('commonCode.createItemDialog.cancel')}
    >
      <div>
        {/* 에러 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 코드 값 (생성 시만 편집 가능) */}
        <div style={fieldStyle}>
          <label htmlFor="item-codeValue" style={labelStyle}>
            {t('commonCode.createItemDialog.codeValue.label')}
            <span
              title={t('commonCode.createItemDialog.codeValue.tooltip')}
              style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
              aria-label={t('commonCode.createItemDialog.codeValue.tooltip')}
            >
              ?
            </span>
          </label>
          <input
            id="item-codeValue"
            type="text"
            placeholder={t('commonCode.createItemDialog.codeValue.placeholder')}
            {...register('codeValue')}
            readOnly={isEdit}
            aria-invalid={!!errorsTyped.codeValue}
            aria-describedby={errorsTyped.codeValue ? 'item-codeValue-error' : 'item-codeValue-hint'}
            style={{
              ...inputStyle,
              fontFamily: 'var(--font-family-mono)',
              borderColor: errorsTyped.codeValue ? 'var(--color-danger)' : 'var(--color-surface-border)',
              opacity: isEdit ? 0.7 : 1,
              cursor: isEdit ? 'default' : 'text',
            }}
          />
          {errorsTyped.codeValue ? (
            <span id="item-codeValue-error" style={errorStyle} role="alert">
              {errorsTyped.codeValue.message}
            </span>
          ) : (
            <span id="item-codeValue-hint" style={hintStyle}>
              {isEdit ? t('commonCode.createItemDialog.codeValue.hintEdit') : t('commonCode.createItemDialog.codeValue.hintCreate')}
            </span>
          )}
        </div>

        {/* 기본 라벨 */}
        <div style={fieldStyle}>
          <label htmlFor="item-defaultLabel" style={labelStyle}>
            {t('commonCode.createItemDialog.defaultLabel.label')}
            <span
              title={t('commonCode.createItemDialog.defaultLabel.tooltip')}
              style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
              aria-label={t('commonCode.createItemDialog.defaultLabel.tooltip')}
            >
              ?
            </span>
          </label>
          <input
            id="item-defaultLabel"
            type="text"
            placeholder={t('commonCode.createItemDialog.defaultLabel.placeholder')}
            {...register('defaultLabel')}
            aria-invalid={!!errorsTyped.defaultLabel}
            aria-describedby={errorsTyped.defaultLabel ? 'item-defaultLabel-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errorsTyped.defaultLabel
                ? 'var(--color-danger)'
                : 'var(--color-surface-border)',
            }}
          />
          {errorsTyped.defaultLabel && (
            <span id="item-defaultLabel-error" style={errorStyle} role="alert">
              {errorsTyped.defaultLabel.message}
            </span>
          )}
        </div>

        {/* i18n 키 */}
        <div style={fieldStyle}>
          <label htmlFor="item-labelKey" style={labelStyle}>
            {t('commonCode.createItemDialog.labelKey.label')}
            <span
              title={t('commonCode.createItemDialog.labelKey.tooltip')}
              style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
              aria-label={t('commonCode.createItemDialog.labelKey.tooltip')}
            >
              ?
            </span>
          </label>
          <input
            id="item-labelKey"
            type="text"
            placeholder={t('commonCode.createItemDialog.labelKey.placeholder')}
            {...register('labelKey')}
            style={{
              ...inputStyle,
              fontFamily: 'var(--font-family-mono)',
            }}
          />
          <span style={hintStyle}>{t('commonCode.createItemDialog.labelKey.hint')}</span>
        </div>

        {/* 표시 순서 + 사용 여부 (2열) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label htmlFor="item-sortOrder" style={labelStyle}>
              {t('commonCode.createItemDialog.sortOrder.label')}
              <span
                title={t('commonCode.createItemDialog.sortOrder.tooltip')}
                style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
                aria-label={t('commonCode.createItemDialog.sortOrder.tooltip')}
              >
                ?
              </span>
            </label>
            <input
              id="item-sortOrder"
              type="number"
              min={0}
              placeholder="0"
              {...register('sortOrder', { valueAsNumber: true })}
              aria-invalid={!!errorsTyped.sortOrder}
              aria-describedby={errorsTyped.sortOrder ? 'item-sortOrder-error' : 'item-sortOrder-hint'}
              style={{
                ...inputStyle,
                borderColor: errorsTyped.sortOrder
                  ? 'var(--color-danger)'
                  : 'var(--color-surface-border)',
              }}
            />
            {errorsTyped.sortOrder ? (
              <span id="item-sortOrder-error" style={errorStyle} role="alert">
                {errorsTyped.sortOrder.message}
              </span>
            ) : (
              <span id="item-sortOrder-hint" style={hintStyle}>
                {t('commonCode.createItemDialog.sortOrder.hint')}
              </span>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-1)',
              justifyContent: 'flex-end',
              paddingBottom: 'var(--spacing-1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <input
                id="item-useYn"
                type="checkbox"
                {...register('useYn')}
                style={{ width: 16, height: 16, accentColor: 'var(--color-brand-primary)', flexShrink: 0 }}
              />
              <label htmlFor="item-useYn" style={{ ...labelStyle, fontWeight: 400, cursor: 'pointer' }}>
                {t('commonCode.createItemDialog.useYn.label')}
              </label>
            </div>
            <span style={hintStyle}>{t('commonCode.createItemDialog.useYn.hint')}</span>
          </div>
        </div>

        {/* 확장 속성 */}
        <div style={fieldStyle}>
          <label htmlFor="item-extraAttrs" style={labelStyle}>
            {t('commonCode.createItemDialog.extraAttrs.label')}
            <span
              title={t('commonCode.createItemDialog.extraAttrs.tooltip')}
              style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
              aria-label={t('commonCode.createItemDialog.extraAttrs.tooltip')}
            >
              ?
            </span>
          </label>
          <textarea
            id="item-extraAttrs"
            placeholder='{"color":"green"}'
            rows={2}
            {...register('extraAttrs')}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 56,
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
            }}
          />
          <span style={hintStyle}>{t('commonCode.createItemDialog.extraAttrs.hint')}</span>
        </div>
      </div>
    </FormDialog>
  );
}
